import type {
  ITeamUpdatePost,
  ITeamUpdateRequestResponse,
  TeamUpdateRequestAssignee,
  TeamUpdateRequestStatus,
} from '../types/TeamUpdate';

/** Resolve assignees from multi-ask field or legacy single-assignee fields. */
export function getTeamUpdateRequestAssignees(
  post: Pick<
    ITeamUpdatePost,
    'requestAssignees' | 'requestAssigneeId' | 'requestAssigneeName'
  >
): TeamUpdateRequestAssignee[] {
  if (post.requestAssignees?.length) {
    const seen = new Set<string>();
    const next: TeamUpdateRequestAssignee[] = [];
    for (const assignee of post.requestAssignees) {
      const userId = assignee.userId?.trim();
      if (!userId || seen.has(userId)) continue;
      seen.add(userId);
      next.push({
        userId,
        name: (assignee.name || 'Member').trim() || 'Member',
      });
    }
    return next;
  }
  const legacyId = post.requestAssigneeId?.trim();
  if (!legacyId) return [];
  return [
    {
      userId: legacyId,
      name: (post.requestAssigneeName || 'Member').trim() || 'Member',
    },
  ];
}

export function getTeamUpdateRequestStatusForUser(
  post: ITeamUpdatePost,
  userId: string,
  responses: ITeamUpdateRequestResponse[]
): TeamUpdateRequestStatus {
  const response = responses.find((row) => row.authorId === userId);
  if (response?.status === 'accepted' || response?.status === 'declined') {
    return response.status;
  }
  // Legacy single-assignee posts stored status on the post itself.
  if (
    post.requestAssigneeId === userId &&
    (post.requestStatus === 'accepted' || post.requestStatus === 'declined')
  ) {
    return post.requestStatus;
  }
  return 'pending';
}

export function formatTeamUpdateRequestAssigneesLabel(
  assignees: TeamUpdateRequestAssignee[]
): string {
  if (assignees.length === 0) return '';
  if (assignees.length === 1) return assignees[0].name;
  if (assignees.length === 2) {
    return `${assignees[0].name} and ${assignees[1].name}`;
  }
  return `${assignees[0].name} +${assignees.length - 1} others`;
}
